// To-dos: the owner's reminders (handover, instalment, renewal notice) as a
// list that can be ticked off. Shared by the Home "Needs your attention" card
// and the To-dos screen in More, so both agree on what is open and what is done.

export interface TodoNotification {
  id: string;
  type: string;
  title: string;
  titleDe?: string;
  message: string;
  messageDe?: string;
  timestamp: Date | string;
  read?: boolean;
  done?: boolean;
  doneAt?: string | null;
}

export interface TodoGroup<T extends TodoNotification = TodoNotification> {
  key: string;
  /** Every notification id behind this row — ticking the row ticks them all. */
  ids: string[];
  n: T;
}

const norm = (s: string | undefined) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * One row per reminder. A watcher that ran twice (two server instances, or a
 * restart before its own history loaded) stores the same reminder more than
 * once; the owner sees one row, and ticking it closes every copy.
 * Newest copy first.
 */
export function groupTodos<T extends TodoNotification>(list: T[]): TodoGroup<T>[] {
  const sorted = [...list].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const groups = new Map<string, TodoGroup<T>>();
  for (const n of sorted) {
    const key = `${n.type}|${norm(n.title)}|${norm(n.message)}`;
    const g = groups.get(key);
    if (g) g.ids.push(n.id);
    else groups.set(key, { key, ids: [n.id], n });
  }
  return Array.from(groups.values());
}

export const todoDoneKey = (investorId?: string) => `todoDone_${investorId || ''}`;

export function readDoneIds(investorId?: string): string[] {
  try { return JSON.parse(localStorage.getItem(todoDoneKey(investorId)) || '[]'); } catch { return []; }
}

export function writeDoneIds(investorId: string | undefined, ids: string[]) {
  try { localStorage.setItem(todoDoneKey(investorId), JSON.stringify(ids.slice(-300))); } catch { /* storage blocked */ }
}

/**
 * Instalments due THIS calendar month that are not marked paid, as to-dos.
 * Only this month: older milestones are not reliable as "unpaid" (SPA-built
 * plans never get their paid status updated), so a backlog would mostly be
 * false alarms. Ids start with `pay-` and are device-only — there is no
 * server notification behind them to tick.
 */
export async function fetchPaymentTodos(apiUrl: string, investorId: string): Promise<TodoNotification[]> {
  const authToken = localStorage.getItem('authToken');
  const res = await fetch(`${apiUrl}/installment-plans/${encodeURIComponent(investorId)}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });
  if (!res.ok) return [];
  const plans = await res.json();
  if (!Array.isArray(plans)) return [];
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const out: TodoNotification[] = [];
  for (const p of plans) {
    for (const m of p.milestones || []) {
      if (!m || !m.dueDate || String(m.dueDate).slice(0, 7) !== ym) continue;
      if (String(m.status || '').toLowerCase() === 'paid') continue;
      const amount = Number(m.amountDue) > 0 ? Number(m.amountDue) : Number(m.amount) || 0;
      if (!(amount > 0)) continue;
      const unit = p.unitName || [p.project, p.unitNumber].filter(Boolean).join(' / ') || 'your unit';
      const cur = p.currency || 'AED';
      const due = new Date(m.dueDate);
      const dueEn = due.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      const dueDe = due.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
      const amt = `${cur} ${Math.round(amount).toLocaleString('en-US')}`;
      const label = m.installmentNumber ? `#${m.installmentNumber}` : (m.description || '');
      out.push({
        id: `pay-${p.id}-${m.installmentNumber || m.dueDate}-${ym}`,
        type: 'payment',
        title: `${unit}: payment due this month`,
        titleDe: `${unit}: Zahlung diesen Monat fällig`,
        message: `${amt} due by ${dueEn}${label ? ` (instalment ${label})` : ''}.`,
        messageDe: `${amt} fällig bis ${dueDe}${label ? ` (Rate ${label})` : ''}.`,
        // Sorts to the top of the list, which runs newest first.
        timestamp: new Date(now.getTime() + 60000),
        read: false,
      });
    }
  }
  return out;
}

export const isLocalTodo = (id: string) => id.startsWith('pay-');

/** Saves the tick (or its undo) on the server; the device copy covers a failure. */
export function saveTodoDone(apiUrl: string, id: string, done: boolean) {
  if (isLocalTodo(id)) return Promise.resolve(undefined);
  const authToken = localStorage.getItem('authToken');
  return fetch(`${apiUrl}/notifications/${encodeURIComponent(id)}/done`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
    body: JSON.stringify({ done }),
  }).catch(() => undefined);
}
